"use client"

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import SignatureService from '@/api/signature.service';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import CreateSignatureForm from './CreateSignatureForm';

export default function EditSignature() {
  const [signature, setSignature] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchSignature = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const response = await SignatureService.getSignature(id);
        
        if (response.data.result === 'success') {
          setSignature(response.data.signature);
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load signature",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching signature:", error);
        toast({
          title: "Error",
          description: "Failed to load signature details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSignature();
  }, [id, toast]);
  
  const handleSuccess = () => {
    toast({
      title: "Success",
      description: "Signature updated successfully",
    });
    navigate(`/signatures/${id}`);
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading signature details...</p>
      </div>
    );
  }
  
  if (!signature) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <p className="text-muted-foreground">Signature not found</p>
        <Button onClick={() => navigate('/signatures')} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Signatures
        </Button>
      </div>
    );
  }
  
  return (
    <div className="container py-8 mx-auto max-w-4xl">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => navigate(`/signatures/${id}`)} 
            variant="ghost" 
            size="sm"
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">
            Edit Signature
          </h1>
        </div>
        
        {/* Edit Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Edit Signature</CardTitle>
          </CardHeader>
          <CardContent>
            <CreateSignatureForm 
              businessId={signature.businessId}
              signatureId={id}
              initialData={signature}
              onSuccess={handleSuccess}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 